import { useState, useEffect, useRef } from "react";

/* GFI Nexus — Click-to-open dropdown button
   ─────────────────────────────────────────────────────────────────
   Used by the detail-panel header action bar (Print PDF ▼ and the
   ⋮ kebab menu) on Shipments / Invoices / Bills. Items can be flat
   or grouped under section headers + an optional small icon.

   Props:
     - trigger   ReactNode     content of the trigger button (icon + label)
     - items     Item[]        flat list (skipped if `sections` is set)
     - sections  Section[]     [{ header?, icon?, items: Item[] }]
     - align     "left"|"right" anchor side of the menu (default right)
     - disabled  bool
     - title     string        tooltip on the trigger
     - style      object        merged into the trigger button style
     - menuStyle  object        merged into the dropdown panel's own style
                                (background/border/boxShadow/etc — e.g. a
                                caller giving the panel a metal-plate look
                                to match a plated trigger)
     - itemHoverBg string       hover background for menu rows (default
                                "#f3f4f6"); pass something readable on a
                                non-white menuStyle background

   Item shape:
     { key, label, onClick, disabled?, icon?, hint?, danger? }

   The menu auto-closes on outside click / Escape. */
export default function ButtonMenu({ trigger, items, sections, align, disabled, title, style, menuStyle: menuStyleOverride, itemHoverBg, itemTextColor, itemTextShadow }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-searchselect]'))) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handlePick = (it) => {
    if (it.disabled) return;
    setOpen(false);
    if (typeof it.onClick === "function") it.onClick();
  };

  /* Normalise the data into one list of "rows": header / item.
     Keeps the rendering loop simple. */
  const rows = [];
  if (Array.isArray(sections) && sections.length > 0) {
    sections.forEach((s, si) => {
      if (s.header || s.icon) rows.push({ type: "header", key: "h" + si, header: s.header, icon: s.icon });
      (s.items || []).forEach((it, i) => rows.push({ type: "item", key: "s" + si + "i" + i, item: it }));
    });
  } else {
    (items || []).forEach((it, i) => rows.push({ type: "item", key: "i" + i, item: it }));
  }

  const triggerStyle = {
    padding: "7px 12px",
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "transparent",
    color: "var(--color-text-secondary)",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 12,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    opacity: disabled ? 0.5 : 1,
    ...(style || {}),
  };

  const menuStyle = {
    position: "absolute",
    top: "100%",
    marginTop: 4,
    [align === "left" ? "left" : "right"]: 0,
    minWidth: 220,
    background: "var(--color-bg-card-solid, #ffffff)",
    border: "1.5px solid #d0d5dd",
    borderRadius: 8,
    boxShadow: "0 12px 32px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.08)",
    zIndex: 1000,
    overflow: "hidden",
    paddingTop: 4,
    paddingBottom: 4,
    ...(menuStyleOverride || {}),
  };
  const rowHoverBg = itemHoverBg || "#f3f4f6";

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        title={title}
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen(!open); }}
        style={triggerStyle}
      >
        {trigger}
      </button>
      {open && (
        <div style={menuStyle}>
          {rows.map((row) => {
            if (row.type === "header") {
              return (
                <div key={row.key} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px 4px", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280", borderBottom: "1px solid #f3f4f6" }}>
                  {row.icon ? <span style={{ display: "inline-flex", alignItems: "center" }}>{row.icon}</span> : null}
                  {row.header ? <span>{row.header}</span> : null}
                </div>
              );
            }
            const it = row.item;
            return (
              <button
                key={row.key}
                type="button"
                disabled={it.disabled}
                onClick={() => handlePick(it)}
                onMouseEnter={(e) => { if (!it.disabled) e.currentTarget.style.background = rowHoverBg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 12px",
                  background: "transparent",
                  border: "none",
                  borderBottom: "none",
                  cursor: it.disabled ? "not-allowed" : "pointer",
                  fontSize: 12,
                  color: it.danger ? "#DC2626" : (itemTextColor || "#1a1a1a"),
                  textShadow: it.danger ? "none" : (itemTextShadow || "none"),
                  opacity: it.disabled ? 0.5 : 1,
                  fontFamily: "inherit",
                }}
              >
                {it.icon ? <span style={{ fontSize: 14, width: 18, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{it.icon}</span> : <span style={{ width: 18 }} />}
                <span style={{ flex: 1, fontWeight: 600 }}>{it.label}</span>
                {it.hint ? <span style={{ fontSize: 10, color: "#9ca3af" }}>{it.hint}</span> : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
